/**
 * DOM Utilities
 * 
 * SSR-safe DOM manipulation utilities that check for
 * browser environment before accessing DOM APIs.
 */

import logger from '../logger.js';

/**
 * Checks if code is running in browser environment
 * @returns {boolean}
 */
export const isBrowser = () => {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
};

/**
 * Gets document element safely (returns null in SSR)
 * @returns {HTMLElement|null}
 */
export const getDocumentElement = () => {
  return isBrowser() ? document.documentElement : null;
};

/**
 * Sets attribute on document element safely
 * @param {string} attribute - Attribute name
 * @param {string} value - Attribute value
 */
export const setDocumentAttribute = (attribute, value) => {
  if (!isBrowser()) {return;}
  const root = document.documentElement;
  if (root) {
    root.setAttribute(attribute, value);
  }
};

/**
 * Removes attribute from document element safely
 * @param {string} attribute - Attribute name
 */
export const removeDocumentAttribute = (attribute) => {
  if (!isBrowser()) {return;}
  const root = document.documentElement;
  if (root) {
    root.removeAttribute(attribute);
  }
};

/**
 * Gets attribute from document element safely
 * @param {string} attribute - Attribute name
 * @returns {string|null}
 */
export const getDocumentAttribute = (attribute) => {
  if (!isBrowser()) {return null;}
  const root = document.documentElement;
  return root ? root.getAttribute(attribute) : null;
};

/**
 * Sets property on document element safely
 * @param {string} property - Property name
 * @param {any} value - Property value
 */
export const setDocumentProperty = (property, value) => {
  if (!isBrowser()) {return;}
  const root = document.documentElement;
  if (root) {
    root[property] = value;
  }
};

/**
 * Gets property from document element safely
 * @param {string} property - Property name
 * @returns {any}
 */
export const getDocumentProperty = (property) => {
  if (!isBrowser()) {return null;}
  const root = document.documentElement;
  return root ? root[property] : null;
};

/**
 * Safely adds event listener to window
 * @param {string} event - Event name
 * @param {Function} handler - Event handler
 * @param {AddEventListenerOptions|boolean} [options] - Event options
 * @returns {Function|null} Cleanup function or null
 */
export const addWindowEventListener = (event, handler, options) => {
  if (!isBrowser()) {return null;}
  
  if (window.addEventListener) {
    window.addEventListener(event, handler, options);
    return () => window.removeEventListener(event, handler, options);
  }
  
  return null;
};

/**
 * Safely removes event listener from window
 * @param {string} event - Event name
 * @param {Function} handler - Event handler
 * @param {AddEventListenerOptions|boolean} [options] - Event options
 */
export const removeWindowEventListener = (event, handler, options) => {
  if (!isBrowser()) {return;}
  if (window.removeEventListener) {
    window.removeEventListener(event, handler, options);
  }
};

/**
 * Safely adds event listener to document
 * @param {string} event - Event name
 * @param {Function} handler - Event handler
 * @param {AddEventListenerOptions|boolean} [options] - Event options
 * @returns {Function|null} Cleanup function or null
 */
export const addDocumentEventListener = (event, handler, options) => {
  if (!isBrowser()) {return null;}
  
  if (document.addEventListener) {
    document.addEventListener(event, handler, options);
    return () => document.removeEventListener(event, handler, options);
  }
  
  return null;
};

/**
 * Safely removes event listener from document
 * @param {string} event - Event name
 * @param {Function} handler - Event handler
 * @param {AddEventListenerOptions|boolean} [options] - Event options
 */
export const removeDocumentEventListener = (event, handler, options) => {
  if (!isBrowser()) {return;}
  if (document.removeEventListener) {
    document.removeEventListener(event, handler, options);
  }
};

/**
 * Gets window width safely
 * @returns {number} Window width or 0 in SSR
 */
export const getWindowWidth = () => {
  return isBrowser() ? window.innerWidth : 0;
};

/**
 * Gets window height safely
 * @returns {number} Window height or 0 in SSR
 */
export const getWindowHeight = () => {
  return isBrowser() ? window.innerHeight : 0;
};

/**
 * Safely creates MediaQueryList and adds listener
 * @param {string} query - Media query string
 * @param {Function} handler - Change handler
 * @returns {Function|null} Cleanup function or null
 */
export const watchMediaQuery = (query, handler) => {
  if (!isBrowser()) {return null;}
  
  try {
    const mediaQuery = window.matchMedia(query);
    
    // Modern API
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
    
    // Legacy API
    if (mediaQuery.addListener) {
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }
    
    // Fallback: call handler immediately with current value
    handler(mediaQuery);
    return null;
  } catch (error) {
    logger.warn('Error creating media query', { error });
    return null;
  }
};

/**
 * Gets system preference for color scheme
 * @returns {'dark'|'light'|null} Color scheme or null in SSR
 */
export const getSystemColorScheme = () => {
  if (!isBrowser()) {return null;}
  
  try {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  } catch (error) {
    logger.warn('Error detecting system color scheme', { error });
    return null;
  }
};

/**
 * Safely accesses localStorage
 * @param {string} key - Storage key
 * @param {any} defaultValue - Default value if key doesn't exist
 * @returns {any}
 */
export const getLocalStorageItem = (key, defaultValue = null) => {
  if (!isBrowser()) {return defaultValue;}
  
  try {
    const item = localStorage.getItem(key);
    return item !== null ? item : defaultValue;
  } catch (error) {
    logger.warn(`Error reading localStorage key "${key}"`, { error, key });
    return defaultValue;
  }
};

/**
 * Safely sets localStorage item
 * @param {string} key - Storage key
 * @param {any} value - Value to store
 * @returns {boolean} Success status
 */
export const setLocalStorageItem = (key, value) => {
  if (!isBrowser()) {return false;}
  
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    logger.warn(`Error writing localStorage key "${key}"`, { error, key });
    return false;
  }
};

/**
 * Safely removes localStorage item
 * @param {string} key - Storage key
 * @returns {boolean} Success status
 */
export const removeLocalStorageItem = (key) => {
  if (!isBrowser()) {return false;}
  
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    logger.warn(`Error removing localStorage key "${key}"`, { error, key });
    return false;
  }
};

